/** Logical canvas for Deadhead Simon (Autobahn lane memory). Matches `autobahn.jpg` (1920×1080). */
export const AUTOBAHN_LANE_SIMON_LOGICAL_WIDTH = 1920;
export const AUTOBAHN_LANE_SIMON_LOGICAL_HEIGHT = 1080;

export const AUTOBAHN_LANE_SIMON_BACKGROUND_SRC = '/minigames/autobahn.jpg';
/** Watch phase: sequence autoplay. */
export const AUTOBAHN_LANE_SIMON_WATCH_SOUND_SRC = '/minigames/race.mp3';
/** Repeat phase: player lane tap. */
export const AUTOBAHN_LANE_SIMON_LANE_PICK_SOUND_SRC = '/sounds/select_skill.mp3';

/** Dev-only: always show lane fills while boundary editor is active. */
export const SHOW_AUTOBAHN_LANE_SIMON_DEV_OUTLINES = false;

/** Server `minigames.autobahnLaneSimon.winPickValue` */
export const AUTOBAHN_LANE_SIMON_WIN_PICK_VALUE = 1;
/** Server `minigames.autobahnLaneSimon.lossPickValue` */
export const AUTOBAHN_LANE_SIMON_LOSS_PICK_VALUE = 0;

/** Server `minigames.autobahnLaneSimon.laneCount` */
export const AUTOBAHN_LANE_SIMON_LANE_COUNT = 4;

/** Server `minigames.autobahnLaneSimon.stepDisplayMs` */
export const AUTOBAHN_LANE_SIMON_STEP_DISPLAY_MS = 700;
/** Server `minigames.autobahnLaneSimon.stepGapMs` */
export const AUTOBAHN_LANE_SIMON_STEP_GAP_MS = 250;

/** One-point perspective vanishing point on `autobahn.jpg` (crescent moon / horizon). */
export const AUTOBAHN_LANE_SIMON_PERSPECTIVE_VANISH_X = 960;
export const AUTOBAHN_LANE_SIMON_PERSPECTIVE_VANISH_Y = 382;
/** Bottom of playable road surface. */
export const AUTOBAHN_LANE_SIMON_PERSPECTIVE_ROAD_BOTTOM_Y = 1065;
/**
 * How far below the vanishing point the lane top edge sits (px).
 * Must be > 0 so lane quads keep non-zero width; smaller = closer to horizon.
 */
export const AUTOBAHN_LANE_SIMON_PERSPECTIVE_ROAD_TOP_OFFSET_BELOW_VANISH_PX = 12;
/** Top of lane hit areas / highlights — extends up to just below the horizon. */
export const AUTOBAHN_LANE_SIMON_PERSPECTIVE_ROAD_TOP_Y =
  AUTOBAHN_LANE_SIMON_PERSPECTIVE_VANISH_Y + AUTOBAHN_LANE_SIMON_PERSPECTIVE_ROAD_TOP_OFFSET_BELOW_VANISH_PX;

/** Minimum px between adjacent boundary lines at the bottom of the image. */
export const AUTOBAHN_LANE_SIMON_MIN_BOUNDARY_GAP_PX = 48;

/**
 * Lane divider / road-edge x at the bottom of the image (5 lines → 4 lanes).
 * Index 0 = left road edge, 1–3 = dashed dividers, 4 = right road edge.
 */
export const DEFAULT_AUTOBAHN_LANE_BOUNDARY_X_BOTTOM = Object.freeze([80, 520, 957, 1415, 1838]);

const LANE_BOUNDARY_X_BOTTOM = DEFAULT_AUTOBAHN_LANE_BOUNDARY_X_BOTTOM;

/**
 * @param {number[]} boundaries
 */
export function assertValidAutobahnLaneBoundaries(boundaries) {
  if (!Array.isArray(boundaries) || boundaries.length !== 5) {
    throw new Error('assertValidAutobahnLaneBoundaries: expected 5 boundary x values');
  }
  let prev = -Infinity;
  for (let i = 0; i < boundaries.length; i += 1) {
    const x = boundaries[i];
    if (!Number.isInteger(x)) {
      throw new Error(`assertValidAutobahnLaneBoundaries: boundary ${i} must be an integer`);
    }
    if (x <= prev) {
      throw new Error(`assertValidAutobahnLaneBoundaries: boundaries must be strictly increasing`);
    }
    if (i > 0 && x - prev < AUTOBAHN_LANE_SIMON_MIN_BOUNDARY_GAP_PX) {
      throw new Error(
        `assertValidAutobahnLaneBoundaries: boundary ${i - 1} and ${i} must be at least ${AUTOBAHN_LANE_SIMON_MIN_BOUNDARY_GAP_PX}px apart`
      );
    }
    prev = x;
  }
  if (boundaries[0] < 0 || boundaries[4] > AUTOBAHN_LANE_SIMON_LOGICAL_WIDTH) {
    throw new Error('assertValidAutobahnLaneBoundaries: boundaries out of canvas range');
  }
}

/**
 * @param {number} xBottom
 * @param {number} y
 * @returns {number}
 */
export function xOnAutobahnPerspectiveBoundary(xBottom, y) {
  const denom = AUTOBAHN_LANE_SIMON_PERSPECTIVE_ROAD_BOTTOM_Y - AUTOBAHN_LANE_SIMON_PERSPECTIVE_VANISH_Y;
  if (denom <= 0) {
    throw new Error('xOnAutobahnPerspectiveBoundary: invalid perspective constants');
  }
  const t = (AUTOBAHN_LANE_SIMON_PERSPECTIVE_ROAD_BOTTOM_Y - y) / denom;
  return xBottom + (AUTOBAHN_LANE_SIMON_PERSPECTIVE_VANISH_X - xBottom) * t;
}

/**
 * @typedef {{ id: number, points: [number, number][] }} AutobahnLaneSlot
 */

/**
 * @param {number[]} boundaries
 * @param {number} laneIndex
 * @returns {AutobahnLaneSlot}
 */
function buildPerspectiveLaneSlotFromBoundaries(boundaries, laneIndex) {
  if (!Number.isInteger(laneIndex) || laneIndex < 0 || laneIndex > 3) {
    throw new Error(`buildPerspectiveLaneSlotFromBoundaries: invalid laneIndex (${String(laneIndex)})`);
  }
  const leftBottom = boundaries[laneIndex];
  const rightBottom = boundaries[laneIndex + 1];
  const leftTop = xOnAutobahnPerspectiveBoundary(leftBottom, AUTOBAHN_LANE_SIMON_PERSPECTIVE_ROAD_TOP_Y);
  const rightTop = xOnAutobahnPerspectiveBoundary(rightBottom, AUTOBAHN_LANE_SIMON_PERSPECTIVE_ROAD_TOP_Y);
  return {
    id: laneIndex,
    points: [
      [leftTop, AUTOBAHN_LANE_SIMON_PERSPECTIVE_ROAD_TOP_Y],
      [rightTop, AUTOBAHN_LANE_SIMON_PERSPECTIVE_ROAD_TOP_Y],
      [rightBottom, AUTOBAHN_LANE_SIMON_PERSPECTIVE_ROAD_BOTTOM_Y],
      [leftBottom, AUTOBAHN_LANE_SIMON_PERSPECTIVE_ROAD_BOTTOM_Y],
    ],
  };
}

/**
 * @param {number[]} [boundaries]
 * @returns {AutobahnLaneSlot[]}
 */
export function buildAutobahnLaneSlotsFromBoundaries(boundaries = LANE_BOUNDARY_X_BOTTOM) {
  const next = [...boundaries];
  assertValidAutobahnLaneBoundaries(next);
  return [
    buildPerspectiveLaneSlotFromBoundaries(next, 0),
    buildPerspectiveLaneSlotFromBoundaries(next, 1),
    buildPerspectiveLaneSlotFromBoundaries(next, 2),
    buildPerspectiveLaneSlotFromBoundaries(next, 3),
  ];
}

/** @type {AutobahnLaneSlot[]} */
export const AUTOBAHN_LANE_SIMON_LANE_SLOTS = buildAutobahnLaneSlotsFromBoundaries(LANE_BOUNDARY_X_BOTTOM);

/**
 * Perspective guide lines for the boundary editor (vanishing point → each bottom x).
 * @param {number[]} boundaries
 * @returns {Array<{ boundaryIndex: number, x1: number, y1: number, x2: number, y2: number }>}
 */
export function buildAutobahnLaneBoundaryGuideLines(boundaries) {
  assertValidAutobahnLaneBoundaries(boundaries);
  return boundaries.map((xBottom, boundaryIndex) => ({
    boundaryIndex,
    x1: AUTOBAHN_LANE_SIMON_PERSPECTIVE_VANISH_X,
    y1: AUTOBAHN_LANE_SIMON_PERSPECTIVE_VANISH_Y,
    x2: xBottom,
    y2: AUTOBAHN_LANE_SIMON_PERSPECTIVE_ROAD_BOTTOM_Y,
  }));
}

/** Dev UI labels for each draggable boundary line. */
export const AUTOBAHN_LANE_BOUNDARY_LABELS = Object.freeze([
  'Left edge (Lane 1)',
  'Lane 1 | 2',
  'Lane 2 | 3',
  'Lane 3 | 4',
  'Right edge (Lane 4)',
]);

/**
 * @param {number[]} boundaries
 * @returns {string}
 */
export function formatAutobahnLaneBoundariesForLayoutFile(boundaries) {
  assertValidAutobahnLaneBoundaries(boundaries);
  const values = boundaries.map((x) => Math.round(x)).join(', ');
  return `export const DEFAULT_AUTOBAHN_LANE_BOUNDARY_X_BOTTOM = Object.freeze([${values}]);`;
}

/**
 * @param {AutobahnLaneSlot} slot
 * @returns {string}
 */
export function autobahnLaneSimonSlotToSvgPoints(slot) {
  if (!slot || !Array.isArray(slot.points) || slot.points.length !== 4) {
    throw new Error('autobahnLaneSimonSlotToSvgPoints: slot.points must have 4 corners');
  }
  return slot.points.map(([x, y]) => `${x},${y}`).join(' ');
}

/**
 * @param {AutobahnLaneSlot} slot
 * @returns {{ left: string, top: string }}
 */
export function autobahnLaneSimonSlotLabelPercentPosition(slot) {
  const [, , rightBottom, leftBottom] = slot.points;
  const x = (leftBottom[0] + rightBottom[0]) / 2;
  const y = AUTOBAHN_LANE_SIMON_PERSPECTIVE_ROAD_BOTTOM_Y - 42;
  return {
    left: `${(x / AUTOBAHN_LANE_SIMON_LOGICAL_WIDTH) * 100}%`,
    top: `${(y / AUTOBAHN_LANE_SIMON_LOGICAL_HEIGHT) * 100}%`,
  };
}
